import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';

const AssignmentForm = ({
    assignment,
    unitId,
    onSave,
    onCancel,
    isSaving
}) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        instructions: '',
        points: 100,
        dueDate: '',
        status: 'published',
        allowLateSubmissions: false,
        latePenalty: 0,
        maxAttempts: 1,
        submissionType: 'both',
        passingScore: 50
    });

    const [activeTab, setActiveTab] = useState('details');

    useEffect(() => {
        if (assignment) {
            const due = new Date(assignment.dueDate);
            // Format datetime-local string (YYYY-MM-DDTHH:mm)
            const formattedDate = new Date(due.getTime() - due.getTimezoneOffset() * 60000)
                .toISOString()
                .slice(0, 16);

            setFormData({
                title: assignment.title || '',
                description: assignment.description || '',
                instructions: assignment.instructions || '',
                points: assignment.points || 100,
                dueDate: formattedDate,
                status: assignment.status || 'published',
                allowLateSubmissions: assignment.allowLateSubmissions || false,
                latePenalty: assignment.latePenalty || 0,
                maxAttempts: assignment.maxAttempts || 1,
                submissionType: assignment.submissionType || 'both',
                passingScore: assignment.passingScore || 50
            });
        }
    }, [assignment]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ ...formData, unitId });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">
                        {assignment ? 'Edit Assignment' : 'Create Assignment'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="flex border-b border-gray-100 px-6">
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Details
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'settings'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Settings & Policies
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <form id="assignment-form" onSubmit={handleSubmit} className="space-y-6">

                        {activeTab === 'details' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        required
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                        placeholder="e.g., Mid-term Project"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        name="description"
                                        rows="3"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                        placeholder="Brief summary of the task..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Detailed Instructions</label>
                                    <textarea
                                        name="instructions"
                                        rows="5"
                                        value={formData.instructions}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                        placeholder="Step-by-step instructions for students..."
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                        <input
                                            type="datetime-local"
                                            name="dueDate"
                                            required
                                            value={formData.dueDate}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                        >
                                            <option value="draft">Draft (Hidden)</option>
                                            <option value="published">Published (Visible)</option>
                                            <option value="archived">Archived</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'settings' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Points</label>
                                        <input
                                            type="number"
                                            name="points"
                                            min="0"
                                            required
                                            value={formData.points}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Passing Score</label>
                                        <input
                                            type="number"
                                            name="passingScore"
                                            min="0"
                                            max={formData.points}
                                            required
                                            value={formData.passingScore}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="border-t border-gray-100 pt-4">
                                    <h4 className="font-medium text-gray-900 mb-3">Submission Rules</h4>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Attempts</label>
                                            <input
                                                type="number"
                                                name="maxAttempts"
                                                min="1"
                                                required
                                                value={formData.maxAttempts}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Submission Type</label>
                                            <select
                                                name="submissionType"
                                                value={formData.submissionType}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                            >
                                                <option value="both">Both File & Text</option>
                                                <option value="file">File Upload Only</option>
                                                <option value="text">Text Entry Only</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-gray-100 pt-4">
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                        Late Submission Policy
                                    </h4>
                                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="allowLate"
                                                name="allowLateSubmissions"
                                                checked={formData.allowLateSubmissions}
                                                onChange={handleChange}
                                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                            />
                                            <label htmlFor="allowLate" className="text-sm text-gray-700">Allow late submissions after due date</label>
                                        </div>

                                        {formData.allowLateSubmissions && (
                                            <div>
                                                <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">Late Penalty (%)</label>
                                                <input
                                                    type="number"
                                                    name="latePenalty"
                                                    min="0"
                                                    max="100"
                                                    value={formData.latePenalty}
                                                    onChange={handleChange}
                                                    className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Percentage deducted from final score.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        )}
                    </form>
                </div>

                <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-lg">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="assignment-form"
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                {assignment ? 'Update Assignment' : 'Create Assignment'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignmentForm;
